import React, { forwardRef, useImperativeHandle, memo, useState, useEffect, useRef, useCallback } from 'react';
import { Platform, View, StyleSheet } from 'react-native';
import Svg, { Path } from 'react-native-svg';

const VIEW_BOX = 4000; // Match AcademyHierarchy viewport

export interface ModuleLinkHandlerHandle {
  setValues: (values: any) => void;
  forceUpdate: () => void;
}

export type ModuleLinkHandlerProps = {
  links: any[];
  modulePositionsRef: React.RefObject<Record<string, { x: number, y: number }>>;  // Direct ref access
  theme?: boolean;
  zoomLevel?: number;
};

const ModuleLinkHandler = forwardRef<ModuleLinkHandlerHandle, ModuleLinkHandlerProps>(({
  links,
  modulePositionsRef,  // Direct ref to positions
  theme = true,
  zoomLevel = 1
}, ref) => {
  const componentMountedRef = useRef(true);
  const [updateTrigger, setUpdateTrigger] = useState(0); // Internal update trigger

  console.log('🔗 ModuleLinkHandler render:', {
    linksCount: links.length,
    hasPositionsRef: !!modulePositionsRef,
    currentPositionsCount: Object.keys(modulePositionsRef.current || {}).length,
    currentPositions: modulePositionsRef.current
  });

  // Immediate update for real-time tracking - no debounce
  const immediateUpdate = useCallback(() => {
    if (componentMountedRef.current) {
      setUpdateTrigger(prev => prev + 1);
    }
  }, []);

  // Handle imperative updates from parent component
  useImperativeHandle(ref, () => ({
    setValues(values: any) {
      console.log('🎯 ModuleLinkHandler setValues (legacy):', values);
      // Legacy method - not needed since we read from ref directly
    },
    forceUpdate() {
      immediateUpdate();
    }
  }));

  // Component cleanup on unmount
  useEffect(() => {
    componentMountedRef.current = true;
    return () => {
      componentMountedRef.current = false;
    };
  }, []);

  if (!links?.length) return null;

  // Don't render until we have positions in the ref
  const currentPositions = modulePositionsRef.current;
  const hasAnyPositions = Object.keys(currentPositions).length > 0;
  if (!hasAnyPositions) {
    console.log('🚫 ModuleLinkHandler: No positions available in ref yet, skipping render');
    return null;
  }

  return (
    <Svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0, // Behind cards
        pointerEvents: 'none' // Allow drag through to underlying components
      }}
    >
      {links.map((link, i) => {
        // Read positions directly from ref - always current!
        // Support both sourceId/targetId formats
        const sourceId = link.sourceId;
        const targetId = link.targetId;
        const source = currentPositions[sourceId];
        const target = currentPositions[targetId];

        console.log('🔍 Processing module link:', {
          linkId: i,
          link: link,
          sourceId,
          targetId,
          hasSource: !!source,
          hasTarget: !!target,
          sourcePosition: source,
          targetPosition: target
        });

        if (!source || !target) {
          // Only log missing positions once per link to reduce console noise
          const linkKey = `${sourceId}-${targetId}`;
          console.log('⚠️ Missing positions for module link:', {
            linkId: i,
            linkKey,
            sourceId,
            targetId,
            hasSource: !!source,
            hasTarget: !!target,
            availablePositions: Object.keys(currentPositions)
          });
          return null;
        }

        // Calculate connection points (center of cards like ActionHierarchy)
        // Module cards are 280x160
        const sourceX = source.x + 140; // Center of card (280px width / 2)
        const sourceY = source.y + 80;  // Center of card (160px height / 2)
        const targetX = target.x + 140;
        const targetY = target.y + 80;

        // Create curved path (cubic bezier) - same style as ActionHierarchy
        const controlX1 = sourceX + (targetX - sourceX) / 2;
        const controlY1 = sourceY + 50;
        const controlX2 = targetX + (sourceX - targetX) / 2;
        const controlY2 = targetY - 50;

        const path = `M ${sourceX} ${sourceY} C ${controlX1} ${controlY1} ${controlX2} ${controlY2} ${targetX} ${targetY}`;

        // Determine link color based on type
        const linkColor = link.type === 'leads_to' ? '#f59e0b' :
          link.type === 'prerequisite' ? '#ef4444' :
            link.type === 'has_submodule' ? '#22c55e' :
              link.type === 'starts_with' ? '#3b82f6' :
                link.color || '#1E40AF';

        return (
          <Path
            key={`${sourceId}-${targetId}-${i}-${updateTrigger}`}
            d={path}
            fill="none"
            stroke={linkColor}
            strokeWidth={link.width || 3}
            opacity={1.0}
            strokeLinecap="round"
            strokeJoin="round"
          />
        );
      })}
    </Svg>
  );
});

ModuleLinkHandler.displayName = 'ModuleLinkHandler';

export default memo(ModuleLinkHandler, (prevProps, nextProps) => {
  // Only re-render if essential props change (not positions or updateTrigger - those are handled imperatively)
  const linksChanged = JSON.stringify(prevProps.links) !== JSON.stringify(nextProps.links);
  const themeChanged = prevProps.theme !== nextProps.theme;
  const zoomChanged = prevProps.zoomLevel !== nextProps.zoomLevel;

  const shouldRerender = linksChanged || themeChanged || zoomChanged;

  return !shouldRerender; // Return true to prevent re-render
});
